// ─── La cassa ────────────────────────────────────────────────────────────────
//
// 🔒 LE DUE REGOLE DEL DENARO (§ 01.6), e qui sono il contratto, non uno stile:
//   ① una riga non si modifica e non si cancella mai — un incasso sbagliato si
//      annulla con uno STORNO, che è una riga nuova;
//   ② `idempotencyKey` è obbligatoria e unica: la stessa richiesta ripetuta
//      trova la chiave già presa e **torna la riga esistente** invece di
//      scriverne un'altra.
//
// 💶 Tutto in CENTESIMI interi. Il segno dice il verso: positivo è denaro che
// entra, negativo è denaro che torna indietro. Così il totale di giornata è una
// somma nuda, senza casi speciali — la forma che regge anche quando qualcuno la
// interroga dalla console invece che da qui.
import { z } from 'zod';
import { dataSchema } from './circolo.js';
import { chiaveIdempotenzaSchema, formatCents } from './money.js';
import { TIPI_TARIFFA } from './tariffe-lezioni.js';
import { VALORE_CREDITO_CENTS, creditiDelGiocatoreSchema } from './premio.js';
// ── I mezzi con cui si paga ─────────────────────────────────────────────────
/**
 * 🎁 `premio` (26/09/2026, voce 33): la quota pagata coi **crediti premio**. Non
 * è un incasso — nessun soldo entra — e resta fuori dai totali della cassa: si
 * vede in una riga a parte (strada 3A).
 */
export const METODI_INCASSO = ['cash', 'card', 'wallet', 'gift', 'premio'];
export const metodoIncassoSchema = z.enum(METODI_INCASSO);
export const ETICHETTE_METODO = {
    cash: 'Contanti',
    card: 'Carta',
    wallet: 'Borsellino',
    gift: 'Omaggio',
    premio: '🎁 Crediti premio',
};
/**
 * I mezzi che NON sono denaro entrato: fuori dal riepilogo e dall'incassato.
 * 🔄 30/09/2026: c'era anche `deposit`, l'acconto scalato dalle quote, andato
 * via con l'acconto (e con lui `sumup`, l'acconto pagato online).
 */
export const METODI_SENZA_INCASSO = new Set(['premio']);
/** L'etichetta di qualunque metodo scritto in cassa. */
export function etichettaMetodo(m) {
    return ETICHETTE_METODO[m] ?? m;
}
/**
 * ⭐⭐ QUALI METODI TOCCANO IL CASSETTO, e non è una sfumatura: è la riga su cui
 * poggia tutto il criterio della Fase 6 («una giornata di incassi veri quadra
 * con quello che la segreteria ha in mano»).
 *
 * · `cash` e `card` sono denaro che si muove **adesso**;
 * · `wallet` è denaro che il circolo ha già incassato **quando il borsellino è
 *   stato ricaricato**: contarlo di nuovo farebbe quadrare la giornata su una
 *   somma che nel cassetto non c'è;
 * · `gift` vale zero e non sposta niente.
 *
 * 📌 Un totale che somma tutti i metodi non è «il totale»: è la somma di due
 *    cose diverse, e chi conta i contanti a fine serata trova una differenza
 *    che non sa spiegare.
 */
export const METODI_CHE_TOCCANO_LA_CASSA = ['cash', 'card'];
/** I mezzi con cui un borsellino si ricarica: quelli che passano dal cassetto. */
export const metodoDiCassaSchema = z.enum(['cash', 'card']);
// ── La riga di cassa ────────────────────────────────────────────────────────
export const incassoSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid().nullable(),
    memberId: z.uuid().nullable(),
    /**
     * ⭐ **QUALE POSTO IN CAMPO ha pagato** — e per un ospite è l'unica identità
     * che esista: il nome non lo è, perché due ospiti possono chiamarsi uguale
     * nella stessa prenotazione ed è ammesso di proposito.
     * ⚠️ `null` sulle righe nate prima del 18/09/2026, e su tutto ciò che non
     * nasce da un posto in campo (una ricarica del borsellino, per dirne una).
     */
    playerId: z.uuid().nullable(),
    /**
     * Il nome scritto sulla riga: c'è anche per gli ospiti, che socio non sono.
     * ⚖️ Resta, e non diventa ridondante: è quello che si LEGGE in cassa, e deve
     * restare leggibile anche quando quel posto in campo non c'è più.
     */
    playerName: z.string().nullable(),
    method: z.string(),
    amountCents: z.number().int(),
    /** 📅 La GIORNATA DI CASSA, non l'istante: un incasso battuto all'una di notte appartiene alla serata prima. */
    businessDate: dataSchema,
    status: z.enum(['paid', 'voided']),
    /** Valorizzato ⇒ questa riga È uno storno, e dice di chi. */
    voidsPaymentId: z.uuid().nullable(),
    /** Valorizzato ⇒ questa riga è stata stornata, e dice da chi. */
    voidedByPaymentId: z.uuid().nullable(),
    recordedAt: z.string(),
    recordedByEmail: z.string().nullable(),
    /** Come si chiama la partita a cui è legato, quando c'è. */
    bookingLabel: z.string().nullable(),
    /**
     * 📅 Il GIORNO della partita, che NON è `businessDate`: un incasso di lunedì
     * può pagare una partita di sabato. ⇒ Serve per aprire il calendario sul
     * giorno giusto dalla riga di cassa; senza, il collegamento porterebbe alla
     * scheda giusta con dietro la giornata sbagliata.
     */
    bookingDate: dataSchema.nullable(),
    reason: z.string().nullable(),
});
/**
 * 🚨 LA CHIAVE DI IDEMPOTENZA DI UN INCASSO È DETERMINISTICA, ed è una scelta
 * OPPOSTA a quella del borsellino (§ `fase-5-anagrafica-borsellino.md` ①) —
 * quindi va spiegata, o la prossima persona ne uniformerà una delle due.
 *
 * · nel **borsellino** la chiave nasce con la finestra: due ricariche allo
 *   stesso socio sono due gesti legittimi, e una chiave deterministica le
 *   farebbe sovrascrivere l'una con l'altra;
 * · in **cassa** no: «Mario paga 8,00 € in contanti per questa partita» è **un
 *   fatto**, non un gesto. Ripeterlo da un'altra scheda, da un altro computer
 *   o il giorno dopo è sempre lo stesso fatto, e deve restare una riga sola.
 *
 * ⚖️ È una protezione che la chiave-della-finestra NON dà: due operatori a due
 * schermi che incassano dalla stessa persona per la stessa partita hanno due
 * finestre diverse, quindi due chiavi diverse — e il socio paga due volte.
 *
 * 🔄 **Il § 01.6 la scriveva `pay|{booking}|{chi}|{metodo}`: qui c'è in più
 * l'IMPORTO, e senza si rompeva un caso vero.** Chi paga 8,00 € adesso e 2,00 €
 * dopo — un acconto, una differenza, una racchetta — manderebbe due volte la
 * stessa chiave, e **la seconda riga sparirebbe senza nessun errore**: il caso
 * peggiore, perché il denaro manca e nessuno lo sa.
 *
 * 🚨🔄⭐⭐ **E L'IMPORTO NON BASTAVA: il 18/09/2026 il banco ha misurato che il
 * caso qui sopra era curato solo a metà.** Distingue 8,00 € da 2,00 €, ma NON
 * due rate UGUALI: 5,00 € adesso e 5,00 € fra un'ora, stesso mezzo, stessa
 * persona, stessa partita ⇒ **stessa chiave**, e la seconda spariva esattamente
 * come descritto due righe più su. 📏 Provato: due incassi da 5,00 € →
 * **una** riga in cassa e **5,00 €** nel cassetto invece di 10,00 €.
 * ⚖️ Lo stesso buco colpiva **due ospiti chiamati allo stesso modo** che pagano
 *   la stessa cifra: dentro un gruppo solo li ferma il controllo dei doppioni,
 *   ma in due gruppi separati il secondo spariva.
 * ⇒ Nella chiave entra **quanto quella persona aveva già pagato su questa
 *   partita nel momento del gesto** (`giaPagatoCents`, storni compresi). È il
 *   pezzo che distingue una rata dall'altra senza rinunciare a niente:
 *   · due dita sullo stesso bottone leggono lo **stesso** già-pagato ⇒ stessa
 *     chiave ⇒ una riga sola;
 *   · due operatori su due schermi, anche con una schermata vecchia, leggono
 *     uno zero tutt'e due ⇒ stessa chiave ⇒ il socio non paga due volte;
 *   · la seconda rata nasce da uno schermo che il primo incasso ha già
 *     aggiornato ⇒ chiave diversa ⇒ passa.
 * 📌 *Una chiave che descrive solo il gesto non sa dire se è già stato fatto;
 *    una che descrive solo il fatto non sa dire se può ripetersi. Questa dice
 *    il gesto **e** da dove partiva.*
 *
 * 📌 Aggiungendo l'importo, ciò che si ripete davvero resta bloccato e ciò che è
 *    diverso passa. Resta legata allo **slot** e non al giorno di cassa: legarla
 *    a «oggi» le farebbe perdere l'idempotenza il giorno dopo.
 *
 * ⚠️ E il terzo caso — **si storna e si rifà lo stesso identico incasso** — qui
 * NON si può vedere: dopo uno storno il già-pagato torna esattamente com'era,
 * quindi la chiave torna identica. A saperlo è solo chi ha la riga stornata
 * davanti, cioè il server ⇒ lo risolve `scriviIncasso` (una riga stornata
 * libera la sua chiave), e le due cure sono complementari, non alternative.
 */
export function chiaveIncasso(i) {
    /**
     * Chi paga, nell'ordine in cui lo si sa dire:
     * ① l'id del **socio**, che è la sua identità e non cambia;
     * ② 🆕 l'id del **posto in campo**, che è l'identità di un ospite — il nome
     *    non lo è, e questo è il pezzo che il 18/09/2026 mancava: due ospiti
     *    chiamati uguale facevano una chiave sola, quindi una riga sola, quindi
     *    un ammanco che non dava errore;
     * ③ il **nome**, normalizzato (o «Mario  Rossi» e «mario rossi» sarebbero due
     *    persone), per chi non ha né l'uno né l'altro.
     *
     * ⚖️ L'ordine non è indifferente, ed è tenuto così apposta: mettendo il posto
     * PRIMA del socio, tutte le chiavi già scritte per i soci cambierebbero
     * forma, e una richiesta in volo attraverso un rilascio scriverebbe una
     * seconda riga. Così invece cambia **solo** ciò che era rotto.
     */
    const chi = i.memberId ?? i.playerId ?? (i.playerName ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    return `pay|${i.bookingId ?? '-'}|${chi === '' ? '-' : chi}|${i.method}|${i.amountCents}|gia:${i.giaPagatoCents}`;
}
export const nuovoIncassoSchema = z.object({
    bookingId: z.uuid().nullable().default(null),
    memberId: z.uuid().nullable().default(null),
    /** ⭐ Il posto in campo che paga, quando l'incasso nasce da una prenotazione. */
    playerId: z.uuid().nullable().default(null),
    playerName: z.string().trim().min(1).max(200).nullable().default(null),
    method: metodoIncassoSchema,
    /**
     * ⛔ Non negativo: un incasso che toglie denaro non è un incasso, è uno
     * storno — e lo storno ha la sua rotta, che sa a quale riga legarsi.
     * `gift` vale zero e nient'altro vale zero (vincolo del database).
     */
    amountCents: z.number().int().min(0, 'Un incasso non può essere negativo'),
    /** Assente = la giornata di cassa di oggi, decisa dal server in ora di Roma. */
    businessDate: dataSchema.optional(),
    reason: z.string().trim().max(200).nullable().default(null),
    idempotencyKey: chiaveIdempotenzaSchema,
});
/**
 * ⛔ L'IMPORTO DELLO STORNO NON SI PASSA: si prende dalla riga che si sta
 * stornando. Se lo scrivesse chi chiama, uno storno potrebbe valere più
 * dell'originale, e la cassa perderebbe denaro da un gesto che si chiama
 * «annulla».
 */
export const stornoIncassoSchema = z.object({
    reason: z.string().trim().max(200).nullable().default(null),
});
/** L'esito di un incasso: la riga, e se è NATA adesso o esisteva già. */
export const esitoIncassoSchema = z.object({
    payment: incassoSchema,
    /**
     * `false` ⇒ questa chiave era già stata usata e si sta rileggendo la riga di
     * allora. La schermata lo dice: «questo incasso era già registrato» non è la
     * stessa frase di «fatto».
     */
    creato: z.boolean(),
    /** Il saldo del borsellino dopo il gesto, quando il gesto l'ha toccato. */
    walletBalanceCents: z.number().int().nullable(),
});
// ── L'incasso di GRUPPO ─────────────────────────────────────────────────────
//
// 🗣️ **NASCE IL 17/09/2026, DA UNA SUA PROPOSTA DISEGNATA**: la scheda della
// prenotazione accanto a un mock con una spunta per giocatore, l'importo, il
// mezzo e **un bottone solo** in fondo. *«Rendere facile e semplice a chi è di
// segreteria effettuare velocemente un incasso […] non bisogna far aprire nuove
// finestre.»*
//
// ⚖️ **Perché è una rotta nuova e non quattro chiamate in fila.** Quattro
// persone che pagano insieme sono **un gesto** al banco: se la terza fallisce
// (borsellino scoperto, rete caduta) con quattro chiamate separate restano due
// righe scritte e due no, e chi sta al banco non sa più a che punto è. Qui o
// passano tutte o non passa nessuna — e a garantirlo è una transazione sola,
// non l'attenzione di chi guarda lo schermo.
// 📌 *Un incasso a metà non è un incasso parziale: è una cassa da ricostruire a
//    memoria.*
export const rigaIncassoInBloccoSchema = z.object({
    memberId: z.uuid().nullable().default(null),
    /** ⭐ Il posto in campo che paga: per un ospite è l'unica identità che c'è. */
    playerId: z.uuid().nullable().default(null),
    playerName: z.string().trim().min(1).max(200).nullable().default(null),
    method: metodoIncassoSchema,
    amountCents: z.number().int().min(0, 'Un incasso non può essere negativo'),
    idempotencyKey: chiaveIdempotenzaSchema,
});
export const incassoInBloccoSchema = z
    .object({
    /** ⛔ Obbligatoria: un incasso di gruppo è l'incasso **di una partita**. */
    bookingId: z.uuid(),
    businessDate: dataSchema.optional(),
    /** Una nota sola, che finisce su tutte le righe: al banco il motivo è uno. */
    reason: z.string().trim().max(200).nullable().default(null),
    righe: z.array(rigaIncassoInBloccoSchema).min(1).max(24),
})
    /**
     * 🚨 DUE RIGHE CON LA STESSA CHIAVE NON SONO DUE RIGHE, e senza questo
     * controllo il blocco perderebbe denaro **in silenzio**: la seconda
     * troverebbe la chiave già presa e tornerebbe la prima con `creato: false`,
     * cioè un incasso che non c'è stato senza nessun errore da nessuna parte.
     * 🔄🚨 **E IL CASO CHE LO MOTIVAVA NON ESISTE PIÙ, dal 18/09/2026.** Qui
     *   c'era scritto: *«succede per davvero: due ospiti chiamati allo stesso modo
     *   … hanno, per costruzione, la stessa chiave»*. Era vero, e questo controllo
     *   li fermava — con un messaggio che diceva **«incassali uno per volta»**,
     *   cioè mandava dritti dentro la perdita che si voleva evitare: incassati uno
     *   per volta, il secondo risultava già pagato dal primo.
     * ⇒ Adesso la chiave porta il **posto in campo** (⇒ `chiaveIncasso`), quindi
     *   due omonimi fanno due chiavi diverse e si incassano **insieme**.
     * ⚖️ **Il controllo resta lo stesso**, ed è giusto che resti: non difendeva
     *   gli omonimi, difendeva l'invariante — due righe con la stessa chiave non
     *   sono due righe. Oggi non scatta più per quel caso; scatterebbe identico
     *   per un browser che mandasse due volte la stessa riga.
     * 📌 *Quando una protezione smette di scattare su un caso, si toglie il caso
     *    dal commento, non la protezione dal codice.*
     */
    .refine((b) => new Set(b.righe.map((r) => r.idempotencyKey)).size === b.righe.length, {
    message: 'Due righe di questo incasso sono identiche: cambiane una, o incassale una per volta.',
    path: ['righe'],
});
export const esitoIncassoInBloccoSchema = z.object({ esiti: z.array(esitoIncassoSchema) });
/**
 * ⭐⭐ **LE STESSE REGOLE DEL SERVER, DETTE PRIMA DEL CLIC** (§ 03.5: «prima di
 * incassare mostra chi paga, quanto, con che metodo, e — se è borsellino — il
 * saldo attuale e quello che resterà»).
 *
 * 🔒 Non **sostituisce** i controlli del server: quelli restano, e sono loro a
 * decidere. Questa funzione serve a non far scoprire uno scoperto **dopo** aver
 * premuto — che è un incasso da stornare, cioè due righe per niente.
 *
 * 💶 E il borsellino si conta **cumulato**: due righe che attingono allo stesso
 * borsellino si sommano fra loro. Guardandole una per una passerebbero
 * entrambe, e la seconda la rifiuterebbe il database a metà gesto.
 */
export function esaminaIncasso(righe, saldi, bookingId, 
/** 🎁 I crediti premio di chi è in campo; assenti ⇒ nessuno ne ha. */
premi = []) {
    const scelte = righe.filter((r) => r.scelto);
    const totaleCents = scelte.reduce((s, r) => s + (r.amountCents ?? 0), 0);
    const esame = (manca) => ({ quante: scelte.length, totaleCents, manca });
    if (scelte.length === 0)
        return esame('Spunta chi sta pagando.');
    for (const r of scelte) {
        if (r.amountCents === null)
            return esame(`L’importo di ${r.displayName} non si capisce.`);
        if (r.method === 'gift' && r.amountCents !== 0) {
            return esame(`Un omaggio vale zero: ${r.displayName} o paga, o è un omaggio.`);
        }
        if (r.method !== 'gift' && r.amountCents === 0) {
            return esame(`Da ${r.displayName} si sta incassando zero: per farlo giocare gratis c’è l’omaggio.`);
        }
        if (r.method === 'wallet' && r.memberId === null) {
            return esame(`Il borsellino è di un socio, e ${r.displayName} è un ospite.`);
        }
        if (r.method === 'premio') {
            if (r.memberId === null)
                return esame(`I Crediti premio sono di un socio, e ${r.displayName} è un ospite.`);
            if (r.amountCents % VALORE_CREDITO_CENTS !== 0) {
                return esame(`Coi Crediti premio si paga a euro interi: 1 Credito = 1,00 €. Correggi l’importo di ${r.displayName}.`);
            }
        }
    }
    // 🎁 I crediti premio, cumulati per socio.
    const daiPremi = new Map();
    for (const r of scelte) {
        if (r.method !== 'premio' || r.memberId === null)
            continue;
        const chiesti = (daiPremi.get(r.memberId) ?? 0) + (r.amountCents ?? 0) / VALORE_CREDITO_CENTS;
        daiPremi.set(r.memberId, chiesti);
        const ha = premi.find((x) => x.memberId === r.memberId)?.credits ?? 0;
        if (chiesti > ha) {
            return esame(`${r.displayName} ha ${ha} Crediti premio: non bastano per ${formatCents(chiesti * VALORE_CREDITO_CENTS)}.`);
        }
    }
    // 💶 Il borsellino, cumulato per socio.
    const daiBorsellini = new Map();
    for (const r of scelte) {
        if (r.method !== 'wallet' || r.memberId === null)
            continue;
        const gia = daiBorsellini.get(r.memberId) ?? 0;
        const chiesto = gia + (r.amountCents ?? 0);
        daiBorsellini.set(r.memberId, chiesto);
        const saldo = saldi.find((s) => s.memberId === r.memberId)?.balanceCents ?? 0;
        if (chiesto > saldo) {
            return esame(`Nel borsellino di ${r.displayName} ci sono ${formatCents(saldo)}: non bastano per ${formatCents(chiesto)}.`);
        }
    }
    // 🚨 Le chiavi doppie: due righe identiche diventerebbero una riga sola, e il
    // denaro della seconda sparirebbe senza nessun errore.
    const viste = new Map();
    for (const r of scelte) {
        const chiave = chiaveIncasso({
            bookingId,
            memberId: r.memberId,
            playerId: r.playerId,
            playerName: r.displayName,
            method: r.method,
            amountCents: r.amountCents ?? 0,
            giaPagatoCents: r.giaPagatoCents,
        });
        const gemella = viste.get(chiave);
        if (gemella !== undefined) {
            return esame(`${gemella} e ${r.displayName} farebbero la stessa identica riga: cambia un importo, o incassali uno per volta.`);
        }
        viste.set(chiave, r.displayName);
    }
    return esame(null);
}
// ── L'elenco ────────────────────────────────────────────────────────────────
export const domandaIncassiSchema = z.object({
    /** La giornata di cassa da cui si guarda. Assente = oggi, in ora di Roma. */
    from: dataSchema.optional(),
    /** Assente = uguale a `from`: una giornata sola è il caso normale. */
    to: dataSchema.optional(),
    method: metodoIncassoSchema.optional(),
    memberId: z.uuid().optional(),
    bookingId: z.uuid().optional(),
    /** `false` = si nascondono le righe stornate e i loro storni. */
    includiStornati: z.stringbool().default(true),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    cursor: z.string().optional(),
});
export const elencoIncassiSchema = z.object({
    items: z.array(incassoSchema),
    total: z.number().int().nullable(),
    nextCursor: z.string().nullable(),
});
// ── Il riepilogo di giornata ────────────────────────────────────────────────
export const totalePerMetodoSchema = z.object({
    method: z.string(),
    righe: z.number().int(),
    totalCents: z.number().int(),
});
/**
 * 🧭 **DA DOVE ARRIVA una riga di cassa** (voce 23, 24/09/2026).
 * 🗣️ *«troveremo sia l'incasso delle partite che l'incasso dei maestri»*.
 *
 * · `partite` — ogni prenotazione che non è di un maestro: la partita, e con
 *   lei il torneo, che si gioca e si paga allo stesso modo;
 * · `maestri` — lezione, allenamento e corso, pagati dagli allievi in
 *   segreteria (⇒ `TIPI_TARIFFA`);
 * · `senza_prenotazione` — le righe che non appartengono a nessuna
 *   prenotazione: ricariche e restituzioni del borsellino.
 *
 * ⚖️ Sono TRE e non due, perché una ricarica non è né una partita né una
 *    lezione, e metterla in una delle due farebbe dire a quella voce una cifra
 *    che non ha incassato. Sommate fanno `totalCents`, come i mezzi: è lo stesso
 *    totale diviso in un altro modo, non un secondo totale.
 */
export const PROVENIENZE = ['partite', 'maestri', 'senza_prenotazione'];
const TIPI_DEI_MAESTRI = TIPI_TARIFFA;
/** Il tipo della prenotazione (o `null` se la riga non ne ha una) ⇒ la sua provenienza. */
export function provenienzaDelTipo(tipo) {
    if (tipo === null)
        return 'senza_prenotazione';
    return TIPI_DEI_MAESTRI.includes(tipo) ? 'maestri' : 'partite';
}
export const totalePerProvenienzaSchema = z.object({
    provenienza: z.enum(PROVENIENZE),
    righe: z.number().int(),
    totalCents: z.number().int(),
});
export const riepilogoCassaSchema = z.object({
    from: dataSchema,
    to: dataSchema,
    perMetodo: z.array(totalePerMetodoSchema),
    /** Sempre tutte e tre, nell'ordine di `PROVENIENZE`, anche quando valgono zero. */
    perProvenienza: z.array(totalePerProvenienzaSchema),
    perGiorno: z.array(z.object({ businessDate: dataSchema, righe: z.number().int(), totalCents: z.number().int() })),
    righe: z.number().int(),
    /**
     * 💶 Il totale di TUTTI i metodi, storni compresi. Non è «quello che c'è nel
     * cassetto»: quello lo dice `contanteCents` + `elettronicoCents`, perché il
     * borsellino è denaro incassato in un altro giorno.
     */
    totalCents: z.number().int(),
    /** Quello che deve esserci in contanti, storni e prelievi già sottratti. */
    contanteCents: z.number().int(),
    /** Quello che deve risultare dal POS. */
    elettronicoCents: z.number().int(),
    /** Quanto borsellino è stato speso: denaro che NON è passato dal cassetto oggi. */
    borsellinoSpesoCents: z.number().int(),
    /**
     * 🎁 Quanto sconto è stato fatto coi crediti premio (strada 3A): a parte,
     * fuori da tutti i totali qui sopra — non è denaro entrato.
     */
    premioUsatoCents: z.number().int(),
    /**
     * 🎾 L'omaggio ai promoter del periodo (16/10/2026, idea approvata): le quote
     * del listino che i promoter non pagano, sulle partite non annullate del
     * periodo. Fuori da tutti i totali: non è denaro, dice quanto costa al circolo.
     */
    omaggioPromoterCents: z.number().int().default(0),
    omaggioPromoterQuote: z.number().int().default(0),
    /** Quanti storni, e quanto pesano: si guardano a parte perché sono la cosa che si spiega a voce. */
    storni: z.number().int(),
    stornatoCents: z.number().int(),
});
export const domandaRiepilogoSchema = z.object({
    from: dataSchema.optional(),
    to: dataSchema.optional(),
});
// ── L'export ────────────────────────────────────────────────────────────────
export const domandaExportCassaSchema = z.object({
    format: z.enum(['csv', 'xlsx']).default('csv'),
    from: dataSchema.optional(),
    to: dataSchema.optional(),
    method: metodoIncassoSchema.optional(),
    memberId: z.uuid().optional(),
    /**
     * 🚨⭐⭐ **IL FILE È UNA COPIA DELL'ELENCO CHE SI STA GUARDANDO, e fino al
     * 18/09/2026 non lo era.** Questo campo non esisteva e la rotta forzava
     * `true`: togliendo la spunta «anche gli storni» l'elenco passava da 5 righe
     * a 3 e **il file ne conteneva 5 lo stesso**.
     * 🗣️ Segnalato da lui: *«mi sa il flag sugli storni non funziona»*. Misurato:
     *   funzionava su un pezzo di schermata su tre.
     * 📌 *Un export che non è la copia di ciò che si vede non è un export: è una
     *    seconda interrogazione, con regole sue, che nessuno ha chiesto.*
     * ⚖️ Il valore predefinito resta **`true`**: chi non dice niente si porta via
     *   tutto, storni compresi, che è ciò che serve alla contabilità — e ciò che
     *   il file faceva prima.
     */
    includiStornati: z.stringbool().default(true),
});
// ── Quello che la scheda della prenotazione mostra della cassa ──────────────
/**
 * 💶 Il dialogo DICE COSA VEDE (§ 03.5): prima di incassare mostra chi paga,
 * quanto, con che mezzo e — se è borsellino — il saldo di adesso e quello che
 * resterà. Un incasso col borsellino che scopre lo scoperto **dopo** il clic è
 * un incasso da stornare.
 * ⇒ Per dirlo, la scheda della prenotazione porta con sé il saldo di ciascuno e
 *   gli incassi già registrati (§ 02.2).
 */
export const saldoDelGiocatoreSchema = z.object({
    memberId: z.uuid(),
    /** `null` = questo socio non ha mai avuto un movimento: non è «zero verificato». */
    balanceCents: z.number().int().nullable(),
});
export const cassaDellaPrenotazioneSchema = z.object({
    payments: z.array(incassoSchema),
    saldi: z.array(saldoDelGiocatoreSchema),
    /** 🎁 I crediti premio spendibili di chi è in campo (solo chi ne ha). */
    premi: z.array(creditiDelGiocatoreSchema).optional(),
    /** La somma di ciò che è già stato incassato su questa partita, storni compresi. */
    incassatoCents: z.number().int(),
});
/**
 * ⭐⭐ QUANTO MANCA DAVVERO: la somma di ciò che manca **a ciascuno**.
 *
 * ⚠️ Non è `a carico − incassato`, e la differenza non è un'inezia: chi ha dato
 * più del suo **non paga il debito di un altro**, e nemmeno lo paga chi è uscito
 * dalla partita lasciando i suoi soldi in cassa. Quella sottrazione può perfino
 * venire **negativa** mentre una persona non ha versato un euro.
 */
export function mancaAQualcuno(quote) {
    return quote.reduce((s, q) => s + Math.max(0, q.amountDueCents - q.incassatoCents), 0);
}
/**
 * 🔄🚨⭐⭐ **CORRETTA IL 18/09/2026, E LA FIRMA È CAMBIATA APPOSTA: prima
 * confrontava DUE SOMME, adesso guarda PERSONA PER PERSONA.**
 *
 * 🗣️ Lo ha visto lui sul gestionale: *«la prenotazione dentro il calendario
 * deve cambiare colore, perché manca un pagamento»*.
 *
 * 📏 **Il difetto, riprodotto sul banco passo per passo**: quattro giocatori a
 * 8,00 €, tutti paganti; se ne toglie uno **senza rimborsarlo** (i suoi soldi
 * restano in cassa, ed è giusto così); poi si **storna** un secondo giocatore,
 * che però resta in campo. Alla fine: a carico 15,00 €, in cassa 15,50 € ⇒
 * `15,50 ≥ 15,00` ⇒ «incassata» ⇒ **verde**, mentre una delle due persone in
 * campo non ha pagato niente. A pareggiare il conto erano i soldi di chi se
 * n'era andato.
 * 📌 *Due somme che tornano non dicono che ciascuno ha pagato: dicono solo che
 *    il totale torna. Sono la stessa cosa finché nessuno esce dal gruppo.*
 *
 * ⚖️ `incassatoCents` — il totale della prenotazione — **serve ancora**, e non è
 * un residuo: è quello che risponde alla domanda *«questa partita ha mosso dei
 * soldi?»*, che è un'altra domanda da *«chi deve ancora pagare?»*.
 */
export function statoIncasso(quote, incassatoCents) {
    const aCaricoCents = quote.reduce((s, q) => s + q.amountDueCents, 0);
    /*
     * ⛔ Il caso strano, e non è teorico: si incassa, e DOPO qualcuno azzera
     * l'importo a carico. Il denaro è entrato davvero ⇒ «incassata», mai
     * «niente»: un blocchetto muto su una partita che ha mosso dei soldi è il
     * modo di perdere di vista proprio quelli.
     */
    if (aCaricoCents <= 0)
        return incassatoCents > 0 ? 'incassata' : 'niente';
    /*
     * 🎁 Una quota pagata coi crediti premio è pagata,
     * anche se non è denaro entrato: `incassatoCents` non la conta, le quote sì.
     * ⇒ Chi ha pagato si guarda persona per persona PRIMA della somma.
     */
    const manca = mancaAQualcuno(quote);
    if (manca === 0)
        return 'incassata';
    if (incassatoCents <= 0 && manca >= aCaricoCents)
        return 'da-incassare';
    return 'in-parte';
}
//# sourceMappingURL=cassa.js.map